import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useState, useEffect, useCallback, useRef } from "react";
import { MdOutlineErrorOutline } from "react-icons/md";
import { useParams } from "react-router-dom";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import { ConfigurePanelBody } from "@/design/components/configure-panel";
import { useAppDispatch, useAppSelector } from "@/reduxHooks";

import { useLazyGetWorkflowVersionsQuery } from "../../api/workflow-api";
import {
  shouldRefreshVersionHistory,
  resetVersionHistoryRefresh,
} from "../../redux";

import VersionHistoryItem from "./version-history-item";

export const VersionHistoryBody = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const shouldRefresh = useAppSelector(shouldRefreshVersionHistory);
  const [
    getWorkflowVersions,
    {
      data: workflowVersions,
      isLoading: isLoadingWorkflowVersions,
      error,
      isFetching,
    },
  ] = useLazyGetWorkflowVersionsQuery();

  const fetchWorkflowVersions = useCallback(async () => {
    await getWorkflowVersions({
      workflowId: params.editorId as string,
      url: nextUrl,
    });
  }, [nextUrl]);

  useEffect(() => {
    fetchWorkflowVersions().catch((e) => {
      console.error("Error fetching workflow versions:", e);
    });
  }, [nextUrl]);

  useEffect(() => {
    if (shouldRefresh) {
      // reset the shouldRefresh flag
      // reset the pagination url to null to refetch 1st page
      // make the api call to fetch 1st page
      dispatch(resetVersionHistoryRefresh());
      setNextUrl(null);
      fetchWorkflowVersions()
        .then(() => {
          virtuosoRef.current?.scrollToIndex({ index: 0, align: "start" });
        })
        .catch((e) => {
          console.error("Error fetching workflow versions:", e);
        });
    }
  }, [shouldRefresh, dispatch, fetchWorkflowVersions]);

  if (isLoadingWorkflowVersions && !nextUrl) {
    return (
      <ConfigurePanelBody overflowY="auto">
        <Flex
          className="bg-gray-50 !w-full p-3 rounded-md gap-3 !self-center"
          align="center"
          justify="center"
        >
          <Spinner className="text-gray-600" size="sm" />
          <Text className="text-gray-600">Fetching version history...</Text>
        </Flex>
      </ConfigurePanelBody>
    );
  }

  if (error) {
    return (
      <ConfigurePanelBody overflowY="auto">
        <Flex
          className="bg-red-50 !w-full p-3 rounded-md gap-3 !self-center border border-red-200"
          align="center"
          justify="center"
        >
          <MdOutlineErrorOutline size={20} className="text-red-500" />
          <Text className="text-red-600">Failed to fetch version history</Text>
        </Flex>
      </ConfigurePanelBody>
    );
  }

  const versionsList = workflowVersions?.response?.data?.runs || [];

  if (versionsList.length === 0 && !nextUrl) {
    return (
      <ConfigurePanelBody overflowY="auto">
        <Flex
          className="bg-gray-50 !w-full p-3 rounded-md gap-3 !self-center border border-gray-200"
          align="center"
          justify="center"
        >
          <MdOutlineErrorOutline size={20} className="text-gray-400" />
          <Text className="text-gray-600">No versions available</Text>
        </Flex>
      </ConfigurePanelBody>
    );
  }

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const loadMore = () => {
    if (!isFetching && workflowVersions?.response?.pagination?.next) {
      setNextUrl(workflowVersions.response.pagination.next);
    }
  };

  return (
    <ConfigurePanelBody overflowY="auto" className="!p-0">
      <Virtuoso
        ref={virtuosoRef}
        style={{ height: "100%", width: "100%" }}
        data={versionsList}
        endReached={loadMore}
        overscan={200}
        itemContent={(index, group) => (
          <Box key={group.id}>
            <VersionHistoryItem
              version={group}
              index={index}
              hasChildren={group.children && group.children.length > 0}
              isOpen={expandedGroups.has(group.id)}
              onToggle={() => toggleGroup(group.id)}
              setNextUrl={setNextUrl}
              scrollToTop={() =>
                virtuosoRef.current?.scrollToIndex({ index: 0, align: "start" })
              }
            />
            {group.children &&
              group.children.length > 0 &&
              expandedGroups.has(group.id) && (
                <Box>
                  {group.children.map((childVersion, childIndex) => (
                    <VersionHistoryItem
                      version={childVersion}
                      index={index}
                      isChild
                      setNextUrl={setNextUrl}
                      scrollToTop={() =>
                        virtuosoRef.current?.scrollToIndex({
                          index: 0,
                          align: "start",
                        })
                      }
                    />
                  ))}
                </Box>
              )}
          </Box>
        )}
        components={{
          Footer: () =>
            isFetching ? (
              <Flex
                className="bg-gray-50 !w-full p-3 rounded-md gap-3 !self-center"
                align="center"
                justify="center"
              >
                <Spinner className="text-gray-600" size="sm" />
                <Text className="text-gray-600">Loading more versions...</Text>
              </Flex>
            ) : null,
        }}
      />
    </ConfigurePanelBody>
  );
};
